import type { RunOutcome } from "@/features/ai-agent/helpers/agent-run";
import type {
  AgentStepSource,
  AgentStepStatus,
  PipTone,
} from "@/features/ai-agent/types/agent-step";

import { AI_ASSISTANT } from "@/config/constants";

/**
 * How a step row is drawn.
 *
 * - `thread` - an Echo session's runs: no icon rail, the run annotates a
 *   conversation it must not compete with.
 * - `panel`  - preview / test-run timelines, where the run IS the subject.
 */
export type TimelineVariant = "thread" | "panel";

export const STEP_PIP_TONE: Record<AgentStepStatus, PipTone> = {
  resolved: "success",
  executed: "success",
  approved: "success",
  answered: "success",
  failed: "danger",
  denied: "danger",
  waiting: "warning",
  running: "idle",
  sending: "idle",
  deferred: "idle",
  superseded: "idle",
};

export const PIP_TONE_CLASS: Record<PipTone, string> = {
  success: "bg-success/55",
  danger: "bg-danger",
  warning: "bg-warning",
  idle: "bg-surface-tertiary",
};

/** Longer runs render this many pips plus a "+N" count - a 40-pip strip reads
 *  as noise, not as shape. */
export const MAX_PIPS = 12;

/** Tone is the step's meaning, not decoration: neutral on the happy path,
 *  coloured only where it changes what you'd do next. Colour only - a filled
 *  chip per row turns a rail of five steps into a column of five buttons. */
export const STEP_TILE_CLASS = {
  default: "text-muted",
  danger: "text-danger",
  warning: "text-warning",
  accent: "text-accent",
} as const;

export type StepTileTone = keyof typeof STEP_TILE_CLASS;

/** Tools whose execution means the agent gave up on answering itself. */
export const HANDOFF_TOOL_IDS = ["handoff_to_human", "escalate_to_human"];

/** Poll cadence for a timeline holding a step that hasn't settled - the safety
 *  net for a missed AGENT_STEP pusher event, which would otherwise strand the
 *  step at "Awaiting review"/"Needs approval", or spinning on "Running", for as
 *  long as the tab stays open. `running` counts: the emit is fire-and-forget on
 *  the backend, so the flip to `resolved` is exactly as droppable as the flip
 *  off `waiting`, and nothing else ever re-asks. A settled timeline never
 *  polls. */
export const UNSETTLED_STEP_POLL_INTERVAL_MS = 30_000;

/** How often a run Echo is working on right now re-reads its steps: the card
 *  shows it live, so a dropped event is healed in seconds, not half a minute. */
export const ECHO_RUN_POLL_INTERVAL_MS = 5_000;

/** Run-level outcomes the backend flags on the message. Rendered by the
 *  timeline itself so the card can't contradict them. */
export const RUN_SIGNALS = [
  {
    key: "handedToHuman",
    label: "Handed to the team",
    icon: "solar:user-hand-up-linear",
    color: "danger",
    hint: `${AI_ASSISTANT.name} handed this conversation to the team`,
  },
  {
    key: "knowledgeGap",
    label: "Missing info",
    icon: "solar:book-bookmark-bold",
    color: "warning",
    hint: `${AI_ASSISTANT.name} had no answer for this. Logged for the knowledge base`,
  },
] as const;

/** Past this many calls in one turn, a group folds by default - four
 *  near-identical lookups are the noisiest, least readable part of a trace. */
export const MAX_UNFOLDED_GROUP_STEPS = 2;

/** Quiet by default, loud only when it needs you. A routine run must not
 *  compete with the conversation around it; a live or blocked one must be
 *  impossible to scroll past. */
export const HEADER_TONE_CLASS = {
  // The Echo orb (`AI_ASSISTANT.icon`) is its identity everywhere else in the
  // inbox (the reply avatar, the assisted badge, the tooltip). The trace mark has
  // to match: with the name dropped it IS the attribution, and it sits directly
  // above the avatar wearing it.
  //
  // No fill behind any of these. A chip around a 13px glyph is chrome competing
  // with the row it labels - colour alone carries the tone.
  idle: {
    row: "",
    hover: "hover:bg-surface-secondary/55",
    tile: "text-secondary",
  },
  live: {
    row: "bg-secondary-soft/25",
    hover: "hover:bg-secondary-soft/40",
    tile: "text-secondary animate-pulse",
  },
  attention: {
    row: "bg-warning-soft/40",
    hover: "hover:bg-warning-soft/60",
    tile: "text-warning",
  },
} as const;

export type HeaderTone = keyof typeof HEADER_TONE_CLASS;

/** `solar:check-bold` renders invisible in this icon set - mdi is the fallback. */
export const RUN_OUTCOME_ICON = {
  replied: { icon: "mdi:check-bold", className: "text-success" },
  handed_off: {
    icon: "solar:users-group-rounded-linear",
    className: "text-warning",
  },
  no_reply: { icon: "solar:minus-circle-linear", className: "text-muted" },
  // Not a failure and not a verdict on the agent - the conversation simply moved
  // on before it finished, so it reads as neutral as `no_reply`.
  cancelled: { icon: "solar:forward-linear", className: "text-muted" },
  live: { icon: "solar:minus-circle-linear", className: "text-muted" },
  // The agent reached out on its own - a completed action, so it reads as
  // positively as `replied` rather than as a neutral stop.
  followed_up: {
    icon: "solar:refresh-circle-linear",
    className: "text-success",
  },
  failed: { icon: "solar:danger-circle-linear", className: "text-danger" },
  // No provable outcome - a neutral full stop, never a verdict.
  unknown: { icon: "solar:check-circle-linear", className: "text-muted" },
} as const satisfies Record<RunOutcome, { icon: string; className: string }>;

/** What became of a session run's reply, as its log line says it. */
export const SESSION_REPLY_STATUS_LABEL: Partial<
  Record<AgentStepStatus, string>
> = {
  executed: "Sent",
  denied: "Discarded",
  superseded: "Replaced",
  waiting: "Draft",
};

/** Sources whose runs happen on a real conversation, so they drive the inbox's
 *  live-run and waiting-approval state. Mirrors the backend's
 *  `DELIVERY_SETTLED_SOURCES`; preview and test runs stay out. */
export const CONVERSATION_STEP_SOURCES: ReadonlySet<AgentStepSource> = new Set([
  "inbox",
  "follow_up",
  "automation",
]);
